import { Alert, Button, Field, Modal, PasswordInput, TextInput } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { changeEmail } from "../../../../api/accountEmails";
import { EmailChangeSentDialog } from "./EmailChangeSentDialog";
import { useEmailFormSubmit } from "./useEmailFormSubmit";

interface ChangeEmailDialogProps {
  current: string;
  /** The address the field starts with: empty for a new change, the pending one to resend it. */
  prefill: string;
  onClose: () => void;
  onSent: () => void;
}

/** Asks for the new primary email and the current password, then says where the link went. */
export function ChangeEmailDialog({ current, prefill, onClose, onSent }: ChangeEmailDialogProps): React.JSX.Element {
  const [email, setEmail] = useState(prefill);
  const [password, setPassword] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { loading, errors, run } = useEmailFormSubmit("We couldn't start the change.");
  const valid = email.trim().length > 0 && password.length > 0;

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    if (!valid) return;
    await run(async () => {
      await changeEmail(email.trim(), password);
      setSentTo(email.trim());
    });
  }

  if (sentTo) return <EmailChangeSentDialog sentTo={sentTo} current={current} onDone={onSent} />;

  return (
    <Modal
      open
      icon="mail"
      title="Change your primary email"
      description="We'll send a link to the new address. Your email changes only when you open it."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Send link" icon="send" loading={loading} disabled={!valid} onClick={() => void submit()} />
        </>
      }
    >
      <form onSubmit={(e) => void submit(e)} className="nb-stack" noValidate>
        <Field label="New email" error={errors.email}>
          <TextInput type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Current password" help="To make sure it's you." error={errors.password}>
          <PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {errors.other ? <Alert tone="danger">{errors.other}</Alert> : null}
      </form>
    </Modal>
  );
}
